import { useState } from "react";
import { openTopLevel } from "@/lib/topNavigation";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/layout/PageHero";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

export const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);
  const { t } = useI18n();

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const next = sessionStorage.getItem("fmo_after_login");
      sessionStorage.removeItem("fmo_after_login");
      navigate(next || "/admin", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 401 ? "err.badCredentials" : status === 429 ? "err.tooMany" : "err.generic");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    openTopLevel(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
  };

  return (
    <div data-testid="admin-login-page">
      <PageHero eyebrow={t("login.eyebrow")} title={t("login.title")} />
      <section className="mx-auto max-w-md px-4 sm:px-0">
        <form onSubmit={submit} className="card-carbon fade-up space-y-5 rounded-3xl p-6 sm:p-8" data-testid="login-form">
          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-red-500/50 bg-red-950/60 px-4 py-3 text-sm text-red-100" role="alert" data-testid="login-error">
              <AlertTriangle className="h-4 w-4 shrink-0" /> {t(error)}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("login.email")}</Label>
            <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-black/50" data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("login.password")}</Label>
            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 bg-black/50" data-testid="login-password-input" />
          </div>
          <button type="submit" className="btn-red w-full" disabled={loading} data-testid="login-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} {t("login.submit")}
          </button>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-neutral-500">
            <span className="h-px flex-1 bg-neutral-700" /> {t("login.or")} <span className="h-px flex-1 bg-neutral-700" />
          </div>
          <button
            type="button"
            onClick={google}
            className="flex w-full items-center justify-center gap-3 rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-semibold text-neutral-900 transition-transform duration-150 hover:-translate-y-0.5 active:translate-y-0"
            data-testid="google-login-btn"
          >
            <GoogleIcon /> {t("login.google")}
          </button>
        </form>
      </section>
    </div>
  );
}
